/* eslint-disable prefer-template */
/* eslint-disable no-param-reassign */
import SearchStorage from '@Services/SearchStorage';
import { replaceCitations } from '@Utils/String';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import UserDatabases from '@Services/UserDatabases';
import AppStorage from '@Services/AppStorage';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import HistoryResource from '@Services/HistoryResource';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import forEach from 'lodash/forEach';
import isUndefined from 'lodash/isUndefined';
import each from 'lodash/each';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import groupBy from 'lodash/groupBy';
import toArray from 'lodash/toArray';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import store from '@Store';
import { SearchTypes, historyReducerKey, RECEIVED_RECENT_HISTORY, ON_PAGE_CHANGE, HANDLE_SEARCH_INPUT } from '@Pages/History/Constants';
import appHistory from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';

const boundOperators = {
  op_num_lesseq: '<=',
  op_num_equal: '=',
  op_num_more: '>',
  op_num_moreeq: '>=',
  op_num_less: '<',
  op_txt_is: 'is',
  op_num_between: 'between',
  op_txt_contains: 'contains',
  op_txt_starts_with: 'starts with',
  op_txt_ends_with: 'ends with',
};

const isSummaryReferencesSearch = (searchType) =>
  searchType === SearchTypes.SUMMARY_REFERENCES || searchType === SearchTypes.SUMMARY_REFERENCED_DOCUMENTS;

const BACKEND_UTC_TIMESTAMP_PATTERN = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

function parseHistoryTimestamp(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const utcMatch = value.match(BACKEND_UTC_TIMESTAMP_PATTERN);
    if (utcMatch) {
      const [, year, month, day, hours, minutes, seconds] = utcMatch;
      return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds));
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

export function isRecentHistoryEntryWithinRetention(item, now = Date.now()) {
  const timestamp = parseHistoryTimestamp(item?.userTime ?? item?.creationTime);
  if (timestamp === null) {
    return true;
  }

  const cutoff = new Date(now);
  cutoff.setUTCMonth(cutoff.getUTCMonth() - 6);
  return timestamp >= cutoff.getTime();
}

function getUtcIsoTimestamp(timeOrItem) {
  let timestamp;

  if (typeof timeOrItem === 'object' && timeOrItem !== null) {
    const creationTime = timeOrItem?.creationTime ?? timeOrItem?.userTime;
    timestamp = parseHistoryTimestamp(creationTime);
  } else {
    timestamp = parseHistoryTimestamp(timeOrItem);
  }

  const date = new Date(Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now());
  const pad = (value) => `${value}`.padStart(2, '0');

  // Keep backend contract format (YYYY/MM/DD HH:mm:ss), but stamp in UTC.
  return `${date.getUTCFullYear()}/${pad(date.getUTCMonth() + 1)}/${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(
    date.getUTCMinutes()
  )}:${pad(date.getUTCSeconds())}`;
}

function defineQuickSearchDescription(searchItem) {
  if (!isUndefined(searchItem.quickSearchItem.content) && searchItem.quickSearchItem.content !== '') {
    searchItem.comment = `${searchItem.comment}${searchItem.quickSearchItem.content}`;
    if (!isUndefined(searchItem.quickSearchItem.structure) && searchItem.quickSearchItem.structure !== '') {
      searchItem.comment += ' AND ';
    }
  }
}

function defineFilteredDescription(searchItem) {
  const appliedFilters = [];
  const appliedFilterNames = [];

  // Facet filters carry the value(s) directly (e.g. "Publication Year: 2024").
  forEach(searchItem.facets, (facet) => {
    if (!facet?.name || appliedFilterNames.indexOf(facet.name) >= 0) {
      return;
    }
    const visibleValues = (facet.values || []).filter((value) => value && !String(value).startsWith('selected-'));
    appliedFilters.push(visibleValues.length ? `${facet.name}: ${visibleValues.join(', ')}` : facet.name);
    appliedFilterNames.push(facet.name);
  });

  forEach(searchItem.filters, (filterVal) => {
    if (filterVal.property === 'YY') {
      appliedFilters.push('Structure');
    } else if (filterVal.property === 'RXD.STP') {
      appliedFilters.push('Single step reactions only');
    } else if (filterVal.title && appliedFilterNames.indexOf(filterVal.title) < 0) {
      appliedFilters.push(filterVal.title);
      appliedFilterNames.push(filterVal.title);
    }
  });

  searchItem.comment += appliedFilters.join(', ');
}

function decamelize(str) {
  return str.replace(/(?:^\w|[A-Z])/g, (letter, index) => (index === 0 ? letter.toUpperCase() : ` ${letter}`));
}
function capitalize(input) {
  if (isString(input)) {
    return decamelize(input);
  }
  return input;
}

function defineContextSwitchDescription(searchItem) {
  if (searchItem.previousResult.query) {
    searchItem.comment = `${searchItem.comment}${searchItem.previousResult.query} - `;
  }
  if (searchItem.previousResult.resultSize) {
    searchItem.comment = `${searchItem.comment}${searchItem.previousResult.resultSize} `;
  }

  if (searchItem.previousResult.context) {
    const context = replaceCitations(searchItem.previousResult.context);

    searchItem.comment += capitalize(context);
  }
}

export function defineFactDescription(searchItem, factItem) {
  // eslint-disable-next-line no-use-before-define
  searchItem.comment = composeFactDescription(searchItem.comment, factItem);
}

export function extractStructuresFromFacts(facts) {
  let structures = [];

  eachHierarchy(
    facts,
    (node) => {
      if (node.structures) {
        structures = structures.concat(node.structures);
      }
    },
    null,
    { children: 'facts' }
  );

  return structures;
}

function _getStructureFromSearch(search, context) {
  if (context && search[context]?.structure) {
    return search[context].structure;
  }
  return search.structure;
}

export function updateItemsComments(databaseStorage) {
  forEach(databaseStorage, (database) => {
    forEach(database.items, (item) => {
      item.comment = sanitizeDiscard(item.comment, { allowedTags: [] });
    });
  });
}

// Stored rows keep the id as a string while the configured collection may key it numerically.
// '' when the id is not in the collection at all (legacy id or licence change).
function resolveConfiguredDatabaseName(databaseId) {
  const storedId = String(databaseId ?? '');
  const numericId = /^\d+$/.test(storedId) ? Number(storedId) : storedId;

  return UserDatabases.getDbName(storedId) || (numericId !== storedId && UserDatabases.getDbName(numericId)) || '';
}

// A legacy id the backend no longer accepts fails the write with "database id {id} not found",
// so point the row at the user's default (Reaxys) database instead of surfacing the error.
export function withResolvableDatabaseId(historyItem) {
  const fallbackId = UserDatabases.getDefaultDatabase()?.id;

  if (resolveConfiguredDatabaseName(historyItem?.databaseId) || fallbackId == null) {
    return historyItem;
  }

  Logger.warn('History: unresolvable databaseId', historyItem?.databaseId, '- using default database', fallbackId);
  return { ...historyItem, databaseId: fallbackId };
}

export function splitHistoryByDBs(historyItems) {
  const groupedItems = groupBy(historyItems, 'databaseId');
  const defaultDatabaseName = UserDatabases.getDefaultDatabase()?.name;
  const formatteditems =
    Object.keys(groupedItems).length > 0 &&
    Object.keys(groupedItems).map((eachKey) => {
      // Backend rows carry no databaseName (DATA-009) but locally retained ones can, and the group
      // only holds the current page slice - so scan for a name rather than trusting index 0.
      const historicalDatabaseName = groupedItems[eachKey]?.find((item) => item?.databaseName)?.databaseName;
      // Never surface a bare legacy id (8, 15) as the heading.
      const name = resolveConfiguredDatabaseName(eachKey) || historicalDatabaseName || defaultDatabaseName;
      return name
        ? {
            databaseId: eachKey,
            priority: UserDatabases.isDefaultDatabase(eachKey) ? '' : name.toLowerCase(),
            databaseName: name,
            items: [...groupedItems[eachKey]],
          }
        : null;
    });
  return sortBy(compact(toArray(formatteditems)), 'priority');
}

function getSavedHistoryTimeValue(item = {}) {
  const saveTime = item?.userTime ?? item?.creationTime;
  if (typeof saveTime === 'number') {
    return saveTime;
  }
  if (typeof saveTime === 'string') {
    return Date.parse(saveTime) || 0;
  }
  return 0;
}
export function formatSavedHistoryData(savedHistoryData) {
  // Order the whole /history/all response newest-first by save time (userTime, falling back to
  // creationTime). splitHistoryByDBs preserves this order within each database group, so a freshly
  // saved entry lands at the top of its group instead of at the end of the raw response.
  const sortedSavedHistoryData = [...savedHistoryData].sort((a, b) => getSavedHistoryTimeValue(b) - getSavedHistoryTimeValue(a));
  sortedSavedHistoryData.forEach((item) => {
    if (item.queryFactItem) {
      const extractedStructures = extractStructuresFromFacts(item.queryFactItem);
      // Preserve response structures when query facts do not contain structure nodes.
      if (extractedStructures.length > 0) {
        item.structures = extractedStructures;
      }
    }
  });
  return splitHistoryByDBs(sortedSavedHistoryData);
}

function setQBSearch(search, historyTypes, historyItem, payload) {
  historyItem.queryFactItem = search.queryFactItem;
  historyItem.historyType = historyTypes.QUERY_BUILDER;
  if (payload) {
    payload.queryFactItem = search.queryFactItem;
    payload.historyType = historyTypes.QUERY_BUILDER;
  }
}

function setSimilaritySearch(search, historyItem, payload) {
  historyItem.structures = _getStructureFromSearch(search, historyItem.context);
  historyItem.queryFactItem = search.queryFactItem;
  historyItem.historyType = SearchTypes.SIMILARITY_SEARCH;
  if (payload) {
    payload.structures = _getStructureFromSearch(search, payload.context);
    payload.queryFactItem = search.queryFactItem;
    payload.historyType = SearchTypes.SIMILARITY_SEARCH;
  }
}

function setReactionsSearch(search, historyItem, payload) {
  historyItem.queryFactItem = search.queryFactItem;
  historyItem.historyType = SearchTypes.REACTIONS_SEARCH;
  if (payload) {
    payload.queryFactItem = search.queryFactItem;
    payload.historyType = SearchTypes.REACTIONS_SEARCH;
  }
}

function setMarkushSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.MARKUSH_SEARCH;
  if (payload) {
    payload.historyType = SearchTypes.MARKUSH_SEARCH;
  }
}

function setMarkushSubstancesSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.MARKUSH_SUBSTANCES;
  if (payload) {
    payload.historyType = SearchTypes.MARKUSH_SUBSTANCES;
  }
}

function setManuallyFiltered(historyItem, payload) {
  historyItem.historyType = SearchTypes.MANUALLY_FILTERED;
  if (payload) {
    payload.historyType = SearchTypes.MANUALLY_FILTERED;
  }
}

function setCrossLinkingSearch(historyItem, payload) {
  historyItem.historyType = SearchTypes.CROSS_LINKING;
  if (payload) {
    payload.historyType = SearchTypes.CROSS_LINKING;
  }
}

function setManualSynthesis(historyItem, payload) {
  historyItem.historyType = SearchTypes.MANUAL_SYNTHESIS;
  if (payload) {
    payload.historyType = SearchTypes.MANUAL_SYNTHESIS;
  }
}

function setSummaryReferences(historyItem, payload) {
  historyItem.historyType = SearchTypes.SUMMARY_REFERENCES;
  if (payload) {
    payload.historyType = SearchTypes.SUMMARY_REFERENCES;
  }
}

function setFiltering(payload, historyTypes, historyItem) {
  payload.historyType = historyTypes.FILTERED;
  payload.filters = historyItem.filters;
  payload.facets = historyItem.facets;
}

function setContextSwitch(payload, historyTypes) {
  payload.historyType = historyTypes.CONTEXT_SWITCH;
}

function setQuickSearch(search, historyTypes, historyItem, payload) {
  const semanticQuery = historyItem.textQuery || search.query || historyItem?.quickSearchItem?.content || '';
  const target = payload || historyItem;
  const storedStructure = historyItem?.quickSearchItem?.structure || historyItem?.structure || target?.quickSearchItem?.structure || '';
  const resolvedStructure = _getStructureFromSearch(search, target.context);

  target.quickSearchItem = {
    ...historyItem?.quickSearchItem,
    content: semanticQuery,
    structure: historyItem.showStructure === false ? '' : resolvedStructure || storedStructure,
    structureUsed: search.structureUsed ?? historyItem?.quickSearchItem?.structureUsed,
    Typeaheads: search.Typeaheads || historyItem?.quickSearchItem?.Typeaheads,
    options: search.options || [],
    databases: UserDatabases.getListWithIds(),
  };
  target.textQuery = semanticQuery;
  if (historyItem.whereClauseImportToken) {
    target.quickSearchItem.whereClauseImportToken = historyItem.whereClauseImportToken;
  }
  target.historyType = historyTypes.QUICK_SEARCH;
}

function getBatchFileLabel(item = {}) {
  if (item?.batchFileName) {
    return item.batchFileName;
  }

  const sourceText = item?.description || item?.comment || '';
  const match = sourceText.match(/Search from file "([^"]+)"/);
  return match?.[1] || '';
}

function setBatchSearch(search, historyTypes, payload, sourceHistoryItem = payload) {
  payload.historyType = historyTypes.BATCH_SEARCH;
  payload.batchFileName = search.batchFileName || getBatchFileLabel(sourceHistoryItem) || getBatchFileLabel(payload);
}

function getSimpleSubstanceStructureDescription(recentSearch, structures) {
  const textFields = [];
  let hasAdvancedExpression = false;

  function collectTextFacts(factItem) {
    factItem?.fields?.forEach((field) => {
      if (field?.value) {
        textFields.push(field);
      }
    });
    if (factItem?.advancedItems?.length) {
      hasAdvancedExpression = true;
    }
    factItem?.facts?.forEach(collectTextFacts);
  }

  collectTextFacts(recentSearch.queryFactItem);
  if (
    recentSearch.context !== 'substances' ||
    structures.length === 0 ||
    hasAdvancedExpression ||
    textFields.length !== 1 ||
    textFields[0].id !== 'SBI.BISUB'
  ) {
    return null;
  }

  const value = String(textFields[0].value).trim();
  const quotedValue = value.startsWith('"') && value.endsWith('"') ? value : `"${value}"`;
  return `${quotedValue} AND `;
}

function defineQueryFactDescription(recentSearch, structures, simpleDescription) {
  if (recentSearch.queryFactItem) {
    recentSearch.structures = structures;
    if (simpleDescription) {
      recentSearch.comment += simpleDescription;
    } else {
      defineFactDescription(recentSearch, recentSearch.queryFactItem);
    }
  }
}

function defineQueryBuilderDescription(recentSearch, structures) {
  const simpleDescription = getSimpleSubstanceStructureDescription(recentSearch, structures);
  recentSearch.comment = `Query Builder: in ${replaceCitations(recentSearch.context)}${simpleDescription ? ' ' : ' - '}`;
  defineQueryFactDescription(recentSearch, structures, simpleDescription);
}

export function getHistoryTypes() {
  const historyTypes = AppStorage.instance.getFromSessionStorage('HistoryTypes');
  if (!historyTypes) {
    return HistoryResource.getHistoryTypes()
      .then((data) => {
        AppStorage.instance.setToSessionStorage('HistoryTypes', data);
        return data;
      })
      .catch(Logger.error);
  }
  return Promise.resolve(historyTypes);
}

export function getSearchDescription(recentSearch) {
  const structures = extractStructuresFromFacts(recentSearch.queryFactItem);

  switch (recentSearch.historyType) {
    case 'quickSearch':
      recentSearch.comment = 'Quick Search: ';

      if (recentSearch.quickSearchItem) {
        defineQuickSearchDescription(recentSearch);
      }
      break;
    case 'filtered':
      recentSearch.comment = 'Filtered by: ';
      if (recentSearch?.filters?.length) {
        defineFilteredDescription(recentSearch);
      }
      break;
    case 'contextSwitch':
      recentSearch.comment = 'Context Switch from: ';
      if (recentSearch.previousResult) {
        defineContextSwitchDescription(recentSearch);
      }
      break;
    case 'queryBuilder':
      defineQueryBuilderDescription(recentSearch, structures);
      break;
    case 'batchSearch':
      if (!recentSearch.comment || !recentSearch.description) {
        const batchFileName = getBatchFileLabel(recentSearch);
        if (batchFileName) {
          recentSearch.comment = `Search from file "${batchFileName}": in ${replaceCitations(recentSearch.context)}`;
        }
      }
      break;
    case 'markush':
      recentSearch.comment = 'Markush structures from substances';
      break;
    case SearchTypes.MARKUSH_SUBSTANCES:
      recentSearch.comment = 'Substances from Markush structure';
      break;
    case 'manually':
      recentSearch.comment = 'Filtered by: manually selected items';
      break;
    case 'cross':
      recentSearch.comment = 'Database Switch from: ';
      if (recentSearch.previousResult) {
        defineContextSwitchDescription(recentSearch);
      }
      break;
    case 'similarity':
      recentSearch.structures = structures.filter((structure) => structure.options.some((option) => option.value === 'similarity'));
      recentSearch.comment = 'Similarity search by selected: ';
      break;
    case SearchTypes.REACTIONS_SEARCH:
      recentSearch.comment = 'Reactions search: ';
      defineQueryFactDescription(recentSearch, structures);
      break;
    case SearchTypes.MANUAL_SYNTHESIS:
      recentSearch.comment = 'From synthesize manually';
      break;
    case SearchTypes.SUMMARY_REFERENCES:
    case SearchTypes.SUMMARY_REFERENCED_DOCUMENTS:
      recentSearch.comment = 'References in summary';
      break;
    default:
      break;
  }
  return recentSearch.comment;
}

const SEARCH_TYPE_FORMATTERS = {
  [SearchTypes.ADVANCED_SEARCH]: (search, historyTypes, historyItem) => setQBSearch(search, historyTypes, historyItem),
  [SearchTypes.SIMILARITY_SEARCH]: (search, historyTypes, historyItem) => setSimilaritySearch(search, historyItem),
  [SearchTypes.REACTIONS_SEARCH]: (search, historyTypes, historyItem) => setReactionsSearch(search, historyItem),
  [SearchTypes.MARKUSH_SEARCH]: (search, historyTypes, historyItem) => setMarkushSearch(historyItem),
  [SearchTypes.QUICK_SEARCH]: (search, historyTypes, historyItem) => setQuickSearch(search, historyTypes, historyItem),
  [SearchTypes.MANUALLY_FILTERED]: (search, historyTypes, historyItem) => setManuallyFiltered(historyItem),
  [SearchTypes.BATCH_SEARCH]: (search, historyTypes, historyItem) => setBatchSearch(search, historyTypes, historyItem),
  [SearchTypes.CROSS_LINKING]: (search, historyTypes, historyItem) => setCrossLinkingSearch(historyItem),
  [SearchTypes.MARKUSH_SUBSTANCES]: (search, historyTypes, historyItem) => setMarkushSubstancesSearch(historyItem),
  [SearchTypes.MANUAL_SYNTHESIS]: (search, historyTypes, historyItem) => setManualSynthesis(historyItem),
};

function applySearchTypeFormatting(search, historyTypes, historyItem) {
  const formatSearch = SEARCH_TYPE_FORMATTERS[search.searchType];
  if (formatSearch) {
    formatSearch(search, historyTypes, historyItem);
    return;
  }

  if (isSummaryReferencesSearch(search.searchType)) {
    setSummaryReferences(historyItem);
    return;
  }

  // when something is forgotten
  Logger.warn('Search type:', search.searchType, ' is not supported');
}

export function formatHistoryData(history, historyTypes) {
  history?.map(async (historyItem) => {
    if (historyItem?.filters?.length) {
      setFiltering(historyItem, historyTypes, historyItem);
    }
    getSearchDescription(historyItem);
    const search = await SearchStorage.get(historyItem.searchId);
    if (search?.searchType && !historyItem.filters) {
      applySearchTypeFormatting(search, historyTypes, historyItem);
    }
  });
}

function setDescriptionIfHasHistory(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS) {
  if (levelHasComplexHistory || fact.historyItems?.length >= COMPLEX_HISTORY_ITEM_MIN_CHUNKS) {
    result.description += 'complex history item combination';
  } else {
    result.description += 'history item';
  }
}

function setDescriptionIfHasFields(fact, result) {
  each(fact.fields, (field, fieldIndex) => {
    if (fieldIndex > 0 && !isUndefined(field.fieldsLogicOperator)) {
      result.description += ' ' + field.fieldsLogicOperator;
      result.description += (field.fieldsLogicOperatorQualifier ? '{' + field.fieldsLogicOperatorQualifier + '}' : '') + ' ';
    }
    result.description += field.id + ' ' + (boundOperators[field.boundOperator] || '=') + ' "' + field.value + '"';
  });
}

function setDescriptionIfExist(fact, result) {
  result.description += ' ' + fact.id + ' exists';
}

function appendOpeningBracket(result, fact, factIndex) {
  if (factIndex === 0) {
    // Prevent from unnecessary space symbol
    const lastDescLetter = result.description.slice(-1);
    result.description += lastDescLetter.match(/\W/g) ? '(' : ' (';
  } else if (!isUndefined(fact.logicOperator)) {
    result.description += ') ' + fact.logicOperator;
    result.description += (fact.logicOperatorQualifier ? '{' + fact.logicOperatorQualifier + '}' : '') + ' (';
  }
}

function appendFactContent(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS) {
  if (!isUndefined(fact.facts) && isUndefined(fact.fields)) {
    // eslint-disable-next-line no-use-before-define
    getFormattedFactItems(fact, result);
  } else if (fact.exist) {
    setDescriptionIfExist(fact, result);
  } else if (!isUndefined(fact.fields) && fact.fields.length) {
    setDescriptionIfHasFields(fact, result);
  } else if (fact?.historyItems?.length) {
    setDescriptionIfHasHistory(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS);
    return levelHasComplexHistory; // signals that isComplexHistoryItemAdded should be set
  }
  return false;
}

export function getFormattedFactItems(factItem, result) {
  const COMPLEX_HISTORY_ITEM_MIN_CHUNKS = 3;

  const historyQLs = filter(factItem.facts, (fact) => fact?.historyItems?.length);
  const levelHasComplexHistory = historyQLs.length >= COMPLEX_HISTORY_ITEM_MIN_CHUNKS;
  let isComplexHistoryItemAdded = false;

  each(factItem.facts, (fact, factIndex) => {
    if (fact?.historyItems?.length && isComplexHistoryItemAdded) {
      if (factIndex + 1 === factItem.facts.length) {
        result.description += ')';
      }
      return;
    }

    appendOpeningBracket(result, fact, factIndex);

    if (appendFactContent(fact, result, levelHasComplexHistory, COMPLEX_HISTORY_ITEM_MIN_CHUNKS)) {
      isComplexHistoryItemAdded = true;
    }

    // Add closing bracket
    if (factIndex + 1 === factItem.facts.length) {
      result.description += ')';
    }
  });
}

function applySearchTypeToPayload(search, historyTypes, historyItem, payload) {
  if (search.searchType === SearchTypes.ADVANCED_SEARCH) {
    setQBSearch(search, historyTypes, historyItem, payload);
  } else if (search.searchType === SearchTypes.SIMILARITY_SEARCH) {
    setSimilaritySearch(search, historyItem, payload);
  } else if (search.searchType === SearchTypes.REACTIONS_SEARCH) {
    setReactionsSearch(search, historyItem, payload);
  } else if (search.searchType === SearchTypes.MARKUSH_SEARCH) {
    setMarkushSearch(historyItem, payload);
  } else if (search.searchType === SearchTypes.QUICK_SEARCH) {
    setQuickSearch(search, historyTypes, historyItem, payload);
  } else if (search.searchType === SearchTypes.BATCH_SEARCH) {
    setBatchSearch(search, historyTypes, payload, historyItem);
  } else if (search.searchType === SearchTypes.MANUALLY_FILTERED) {
    setManuallyFiltered(historyItem, payload);
  } else if (search.searchType === SearchTypes.CROSS_LINKING) {
    setCrossLinkingSearch(historyItem, payload);
  } else if (search.searchType === SearchTypes.MARKUSH_SUBSTANCES) {
    setMarkushSubstancesSearch(historyItem, payload);
  } else if (search.searchType === SearchTypes.MANUAL_SYNTHESIS) {
    setManualSynthesis(historyItem, payload);
  } else if (isSummaryReferencesSearch(search.searchType)) {
    setSummaryReferences(historyItem, payload);
  } else {
    // when something is forgotten
    Logger.warn('Search type:', search.searchType, ' is not supported');
  }
}

export function formatNewHistory(historyItem, newSearchName, useCurrentTime = false) {
  return SearchStorage.get(historyItem.searchId)
    .then((search) =>
      getHistoryTypes()
        .then((historyTypes) => {
          const storedHistoryType =
            typeof historyItem?.historyType === 'string' ? historyItem.historyType.trim() : historyItem?.historyType;
          const payload = {
            databaseId: historyItem.databaseId,
            context: historyItem.context,
            hitSetKey: historyItem.hitSetKey,
            historyType: storedHistoryType || historyTypes.CONTEXT_SWITCH,
            resultSize: historyItem.resultSize || historyItem.totalResult,
            // Saving a query stamps the save-moment; other callers keep the search's own creationTime.
            userTime: useCurrentTime ? getUtcIsoTimestamp() : getUtcIsoTimestamp(historyItem),
            comment: historyItem.comment,
            searchName: newSearchName,
            resultName: historyItem.resultName,
            searchId: historyItem.searchId,
            order: historyItem.order,
            sort: historyItem.sort,
            totalResult: historyItem.totalResult ?? historyItem.resultSize ?? historyItem.resultsCount ?? historyItem.recordCount,
            structure: _getStructureFromSearch(search, historyItem.context),
            // Source of the context/database switch description below. Breadcrumb sets it on the new
            // crumb; without it here getSearchDescription can only emit the bare "... from: " prefix.
            // Transient — stripped before the Recent History POST.
            previousResult: historyItem.previousResult,
          };

          if (search?.searchType && !historyItem?.filters) {
            applySearchTypeToPayload(search, historyTypes, historyItem, payload);
          } else if (historyItem.filters) {
            setFiltering(payload, historyTypes, historyItem);
          } else if (!storedHistoryType) {
            setContextSwitch(payload, historyTypes);
          }

          // Generate the comment from the formatted payload. Search-type formatting above adds
          // fields (notably batchFileName) that are not guaranteed to exist on a saved-history row.
          getSearchDescription(payload);
          return payload;
        })
        .catch(Logger.reject)
    )
    .catch(Logger.reject);
}

function formatOperator(operator, qualifier) {
  const qualifierStr = qualifier ? `{${qualifier}}` : '';
  return ` ${operator}${qualifierStr} `;
}

function appendFieldsChain(fact, factIndex, factItem) {
  let result = '';
  each(fact.fields, (field, fieldIndex) => {
    result += `${field.displayName} "${field.value}"`;
    if (fieldIndex + 1 < fact.fields.length && !isUndefined(fact.fieldsLogicOperator)) {
      result += formatOperator(fact.fieldsLogicOperator, fact.fieldsLogicOperatorQualifier);
    }
  });
  if (factIndex + 1 === factItem.facts.length && !isUndefined(fact.logicOperator) && !isUndefined(factItem.structures)) {
    result += formatOperator(fact.logicOperator, fact.logicOperatorQualifier);
  }
  return result;
}

function getFactAddition(fact, factIndex, factItem) {
  let addition = '';
  if (factIndex === 0 && factItem.logicOperator) {
    addition += formatOperator(factItem.logicOperator, factItem.logicOperatorQualifier);
  }
  if (factIndex > 0 && fact.logicOperator) {
    addition += formatOperator(fact.logicOperator, fact.logicOperatorQualifier);
  }
  if (!isUndefined(fact.fields) && fact.fields.length) {
    addition += appendFieldsChain(fact, factIndex, factItem);
  } else if (!isUndefined(fact.advancedItems) && fact.advancedItems.length) {
    addition += `"${fact.advancedItems[0].advancedExpression}"`;
  }
  return addition;
}

// eslint-disable-next-line default-param-last
export function composeFactDescription(comment = '', factItem) {
  each(factItem.facts, (fact, factIndex) => {
    if (!isUndefined(fact.facts) && isUndefined(fact.fields)) {
      composeFactDescription(comment, fact);
    } else {
      comment += getFactAddition(fact, factIndex, factItem);
    }
  });
  return comment;
}

function hasTextFact(factItem) {
  return Boolean(factItem?.fields?.some((field) => field?.value) || factItem?.facts?.some((nestedFact) => hasTextFact(nestedFact)));
}

function getSemanticTextQuery(query) {
  const semanticQueryBySearchId = AppStorage.instance.getFromSessionStorage('qbSemanticQueryBySearchId') || {};
  return query?.textQuery || query?.semanticQuery || semanticQueryBySearchId[query?.searchId] || '';
}

function isSemanticHistoryQuery(query) {
  if (query?.semanticSearchIncluded === false && !query?.isSemanticSearchIncluded) {
    return false;
  }
  return Boolean(query?.semanticSearchIncluded || query?.isSemanticSearchIncluded || getSemanticTextQuery(query));
}

function hasField(factItem, fieldId) {
  return Boolean(
    factItem?.fields?.some((field) => field?.id === fieldId) || factItem?.facts?.some((nestedFact) => hasField(nestedFact, fieldId))
  );
}

function restoreSemanticQuery(importedQuery, sourceQuery) {
  const textQuery = getSemanticTextQuery(sourceQuery);
  if (!isSemanticHistoryQuery(sourceQuery) || !textQuery) {
    return importedQuery;
  }

  const restoredImport = cloneDeep(importedQuery);
  const queryFactItem = restoredImport?.queryFactItem || restoredImport;
  if (!queryFactItem || hasField(queryFactItem, 'CIT.NLQL')) {
    return { ...restoredImport, isSemanticSearchIncluded: true };
  }

  const trimmedTextQuery = String(textQuery).trim();
  const nlqlValue = trimmedTextQuery.startsWith('"') && trimmedTextQuery.endsWith('"') ? trimmedTextQuery : `"${trimmedTextQuery}"`;
  const nlqlFact = {
    id: 'Reaxys2071',
    logicOperator: 'OR',
    fields: [
      {
        value: nlqlValue,
        boundOperator: 'op_txt_is',
        id: 'CIT.NLQL',
        displayName: 'Natural Language Document Search',
        imported: true,
        isSemanticSearchIncluded: true,
      },
    ],
    fieldsLogicOperator: 'AND',
    exist: false,
    bio: false,
    isSemanticSearchIncluded: true,
  };
  const rootFacts = queryFactItem.facts || [];
  const firstFact = rootFacts[0];
  const restoredQueryFactItem = Array.isArray(firstFact?.facts)
    ? { ...queryFactItem, facts: [{ ...firstFact, facts: [...firstFact.facts, nlqlFact] }, ...rootFacts.slice(1)] }
    : { ...queryFactItem, facts: [...rootFacts, nlqlFact] };

  return restoredImport?.queryFactItem
    ? { ...restoredImport, queryFactItem: restoredQueryFactItem, isSemanticSearchIncluded: true }
    : { ...restoredQueryFactItem, isSemanticSearchIncluded: true };
}

function restoreQuickSearchStructure(queryFactItem, sourceQuery) {
  const sourceStructure = sourceQuery.quickSearchItem?.structure;
  let hasStructureNode = false;

  function restore(factItem) {
    if (factItem?.structures?.length) {
      hasStructureNode = true;
      factItem.structures.forEach((structure) => {
        if (!structure.structure) {
          structure.structure = sourceStructure;
        }
      });
    }
    factItem?.facts?.forEach(restore);
  }

  restore(queryFactItem);
  if (!hasStructureNode && sourceStructure) {
    queryFactItem.facts = [
      {
        id: 'wrapper',
        structures: [{ id: 'STRUCTURE.QL', structure: sourceStructure, options: sourceQuery.quickSearchItem?.options || [] }],
      },
      ...(queryFactItem.facts || []),
    ];
  }
}

function restoreSubstanceQuickSearch(importedQuery, sourceQuery) {
  const content = sourceQuery.quickSearchItem?.content?.trim();
  const restoredImport = cloneDeep(importedQuery);
  const queryFactItem = restoredImport?.queryFactItem || restoredImport;

  if (sourceQuery.context !== 'substances' || isSemanticHistoryQuery(sourceQuery) || !queryFactItem) {
    return importedQuery;
  }

  restoreQuickSearchStructure(queryFactItem, sourceQuery);

  if (!content || hasTextFact(queryFactItem)) {
    return restoredImport;
  }

  const facts = queryFactItem.facts || [];
  const contentFact = {
    id: 'Reaxys1',
    exist: false,
    fieldsLogicOperator: 'AND',
    fields: [
      {
        id: 'SBI.BISUB',
        value: content,
        boundOperator: 'op_txt_is',
        displayName: 'Substance Basic Index',
      },
    ],
    ...(facts.length ? { logicOperator: 'AND' } : {}),
  };
  const restoredQueryFactItem = { ...queryFactItem, facts: [...facts, contentFact] };

  return restoredImport?.queryFactItem ? { ...restoredImport, queryFactItem: restoredQueryFactItem } : restoredQueryFactItem;
}

export function copyQuery(query) {
  function done(queryVal) {
    const importedQuery = restoreSemanticQuery(queryVal, query);

    appHistory.push({
      pathname: paths.ADVANCED,
      state: { importedQuery: { ...importedQuery, replaceExistingQuery: true } },
    });
  }
  const hasQuickSearchImportToken = Boolean(query.quickSearchItem?.whereClauseImportToken);
  const semanticQuery = getSemanticTextQuery(query);

  // Quick Search result rows can also carry a queryFactItem used to execute one result bucket.
  // That payload may contain only the structure even though quickSearchItem contains both text and
  // structure. Prefer the exact executed where-clause for these hybrid rows so Edit in Query Builder
  // restores the complete Quick Search query. RX-25421
  if (isObject(query.queryFactItem) && !hasQuickSearchImportToken) {
    done(query);
  } else {
    AdvancedQuery.getQueryByWhereClause({
      ...query,
      ...(isSemanticHistoryQuery(query) && semanticQuery ? { semanticQuery } : {}),
    })
      .then((whereClauseImport) => {
        if (whereClauseImport) {
          const completeImport = restoreSubstanceQuickSearch(whereClauseImport, query);
          completeImport.context = query.context;
          if (query.resolvedByCasApi) {
            completeImport.resolvedByCasApi = query.resolvedByCasApi;
          }
          done(completeImport);
        }
      })
      .catch(Logger.error);
  }
}

const pendingSaves = new Map();
const saveTimers = new Map();
const pendingSavePromises = new Map();

const RECENT_SAVED_SIGNATURES_KEY = 'recentHistorySavedSignatures';
const RECENT_SESSION_ONLY_KEY = 'recentHistorySessionOnly';
const ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY = 'anonymousRecentHistoryUpgradeSnapshot';
const ANONYMOUS_RECENT_HISTORY_RESET_KEY = 'anonymousRecentHistoryReset';
const HISTORY_TYPES_SESSION_KEY = 'HistoryTypes';
let recentHistoryCache = null;

function recentSaveSignature(data) {
  return `${data.resultName}|${data.sort || ''}|${data.order || ''}`;
}

function isQuickSearchHistory(item = {}) {
  return Boolean(item?.quickSearchItem) || item?.historyType === 'quickSearch' || item?.historyType === SearchTypes.QUICK_SEARCH;
}

function getRecentHistoryIdentity(item = {}) {
  return `${item?.databaseId || ''}|${item?.resultName || ''}|${item?.creationTime || ''}`;
}

function getCreationTimeValue(item = {}) {
  const creationTime = item?.userTime ?? item?.creationTime;
  return parseHistoryTimestamp(creationTime) ?? 0;
}

function getBatchHistoryMergeKey(item = {}) {
  const isBatchHistory = item?.historyType === SearchTypes.BATCH_SEARCH || item?.historyType === 'batchSearch';
  const fileLabel = getBatchFileLabel(item);
  if (!isBatchHistory || !fileLabel) {
    return '';
  }

  return `batch|${item?.context || ''}|${item?.databaseId || ''}|${fileLabel}`;
}

function getDerivedRecentHistoryMergeKey(item = {}) {
  const hasFilters = item?.filters?.length || item?.facets?.length;
  const isSortOrderOnlyOverlay =
    item?.isSessionOnlyRecent && !hasFilters && (item?.sort || item?.order) && (item?.searchId || getCreationTimeValue(item));
  const isDerivedRecentRow = hasFilters || (item?.isSessionOnlyRecent && !isSortOrderOnlyOverlay);

  if (!isDerivedRecentRow) {
    return '';
  }

  const timestamp = getCreationTimeValue(item);
  return [
    'derived',
    item?.databaseId || '',
    item?.resultName || '',
    timestamp || '',
    item?.sort || '',
    item?.order || '',
    JSON.stringify(item?.filters || []),
    JSON.stringify(item?.facets || []),
  ].join('|');
}

function getRerunSourceRecentHistoryMergeKey(item = {}) {
  const source = item?.rerunSourceRecentHistory;
  if (!source) {
    return '';
  }

  const sourceBatchMergeKey = getBatchHistoryMergeKey(source);
  if (sourceBatchMergeKey) {
    return sourceBatchMergeKey;
  }

  const sourceDerivedMergeKey = getDerivedRecentHistoryMergeKey(source);
  if (sourceDerivedMergeKey) {
    return sourceDerivedMergeKey;
  }

  if (source?.searchId) {
    return `search|${source?.databaseId || ''}|${source?.context || ''}|${source.searchId}`;
  }

  if (source?.resultName) {
    return `rerun-source|${source?.databaseId || ''}|${source.resultName}`;
  }

  return '';
}

function getRecentHistoryMergeKey(item = {}) {
  const rerunSourceMergeKey = getRerunSourceRecentHistoryMergeKey(item);
  if (rerunSourceMergeKey) {
    return rerunSourceMergeKey;
  }

  const batchMergeKey = getBatchHistoryMergeKey(item);
  if (batchMergeKey) {
    return batchMergeKey;
  }

  const derivedMergeKey = getDerivedRecentHistoryMergeKey(item);
  if (derivedMergeKey) {
    return derivedMergeKey;
  }

  if (item?.searchId) {
    return `search|${item?.databaseId || ''}|${item?.context || ''}|${item.searchId}`;
  }

  const timestamp = getCreationTimeValue(item);
  return timestamp || item;
}

function isSameRerunSourceRecentSearch(left = {}, source = {}) {
  if (!source || !Object.keys(source).length) {
    return false;
  }

  const sourceMergeKey = getRerunSourceRecentHistoryMergeKey({ rerunSourceRecentHistory: source });
  const leftSourceMergeKey = getRerunSourceRecentHistoryMergeKey(left);
  if (sourceMergeKey && leftSourceMergeKey && sourceMergeKey === leftSourceMergeKey) {
    return true;
  }

  const sourceBatchMergeKey = getBatchHistoryMergeKey(source);
  if (sourceBatchMergeKey) {
    return getBatchHistoryMergeKey(left) === sourceBatchMergeKey;
  }

  const sourceDerivedMergeKey = getDerivedRecentHistoryMergeKey(source);
  if (sourceDerivedMergeKey) {
    return getDerivedRecentHistoryMergeKey(left) === sourceDerivedMergeKey;
  }

  if (source?.id && left?.id) {
    return left.id === source.id;
  }

  if (left?.searchId && source?.searchId) {
    return (
      `${left.databaseId || ''}` === `${source.databaseId || ''}` && left.context === source.context && left.searchId === source.searchId
    );
  }

  if (source?.resultName) {
    return `${left?.databaseId || ''}` === `${source.databaseId || ''}` && left.resultName === source.resultName;
  }

  return false;
}

function isSameRerunRecentSearch(left = {}, right = {}) {
  if (isSameRerunSourceRecentSearch(left, right?.rerunSourceRecentHistory)) {
    return true;
  }

  if (left?.searchId && right?.searchId) {
    return `${left.databaseId || ''}` === `${right.databaseId || ''}` && left.context === right.context && left.searchId === right.searchId;
  }

  return (
    `${left?.databaseId || ''}` === `${right?.databaseId || ''}` &&
    left?.resultName === right?.resultName &&
    recentSaveSignature(left) === recentSaveSignature(right)
  );
}

function sortRecentHistoryEntries(entries = [], direction = 'asc') {
  const factor = direction === 'desc' ? -1 : 1;
  return [...entries].sort((left, right) => (getCreationTimeValue(left) - getCreationTimeValue(right)) * factor);
}
function mergeRecentHistoryEntry(existingEntry, entry) {
  if (!existingEntry) {
    return entry;
  }

  const mergedEntry = { ...existingEntry, ...entry };
  const existingIsBackendEntry = existingEntry?.id && !existingEntry?.isSessionOnlyRecent;
  if (existingIsBackendEntry) {
    mergedEntry.id = existingEntry.id;
    delete mergedEntry.isSessionOnlyRecent;
  }

  return mergedEntry;
}

export function mergeRecentHistoryEntries(primaryEntries = [], secondaryEntries = [], direction = 'asc') {
  const mergedEntries = new Map();

  [...primaryEntries, ...secondaryEntries].forEach((entry) => {
    if (!entry) {
      return;
    }
    const key = getRecentHistoryMergeKey(entry);
    const existingEntry = mergedEntries.get(key);
    mergedEntries.set(key, mergeRecentHistoryEntry(existingEntry, entry));
  });

  return sortRecentHistoryEntries([...mergedEntries.values()], direction);
}

export function getRecentHistoryCache() {
  return recentHistoryCache === null ? null : [...recentHistoryCache];
}

export function setRecentHistoryCache(entries = []) {
  recentHistoryCache = [...entries];
  return getRecentHistoryCache();
}

export function clearRecentHistoryCache() {
  recentHistoryCache = null;
}

export function filterRecentHistoryEntries(entries = [], query = '') {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return entries;
  }

  return entries.filter((entry) =>
    [entry?.searchName, entry?.comment, entry?.description, entry?.textQuery, entry?.resultName, entry?.quickSearchItem?.content].some(
      (value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedQuery)
    )
  );
}

function isSameRecentHistoryEntry(entry, target) {
  if (typeof target === 'string') {
    return entry?.id === target || entry?.resultName === target;
  }

  const targetTimestamp = getCreationTimeValue(target);
  if (targetTimestamp) {
    return getCreationTimeValue(entry) === targetTimestamp;
  }
  if (target?.id) {
    return entry?.id === target.id;
  }
  return Boolean(target?.resultName) && getRecentHistoryIdentity(entry) === getRecentHistoryIdentity(target);
}

function getSavedRecentHistoryId(result) {
  if (typeof result === 'string') {
    return result;
  }
  return result?.id ?? result?.payload?.id ?? result?.data?.id;
}

export async function cancelPendingRecentHistorySaves(entriesToRemove = []) {
  const targets = (Array.isArray(entriesToRemove) ? entriesToRemove : [entriesToRemove]).filter(Boolean);
  const completedSaveIds = [];
  const savesToWaitFor = [];

  pendingSaves.forEach((pending, key) => {
    const pendingMergeKey = getRecentHistoryMergeKey(pending);
    const matchesTarget = targets.some((target) => {
      const targetMergeKey = getRecentHistoryMergeKey(target);
      return isSameRecentHistoryEntry(pending, target) || pendingMergeKey === targetMergeKey;
    });
    if (!matchesTarget) {
      return;
    }

    const timer = saveTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      saveTimers.delete(key);
      pendingSaves.delete(key);
      return;
    }

    const savePromise = pendingSavePromises.get(key);
    if (savePromise) {
      savesToWaitFor.push(
        savePromise.then((result) => {
          const savedId = getSavedRecentHistoryId(result) || pending?.id;
          if (savedId) {
            completedSaveIds.push(savedId);
          }
        })
      );
    }
  });

  await Promise.all(savesToWaitFor);
  return [...new Set(completedSaveIds)];
}

export function getRecentHistoryBackendIds(entriesToRemove = [], backendEntries = []) {
  const targets = (Array.isArray(entriesToRemove) ? entriesToRemove : [entriesToRemove]).filter(Boolean);
  const backendIds = targets.flatMap((target) => {
    if (typeof target === 'string') {
      return [target];
    }
    if (target?.isSessionOnlyRecent) {
      return [];
    }

    const targetMergeKey = getRecentHistoryMergeKey(target);
    const matchingBackendIds = backendEntries
      .filter((entry) => entry?.id && getRecentHistoryMergeKey(entry) === targetMergeKey)
      .map((entry) => entry.id);
    return [...(target?.id ? [target.id] : []), ...matchingBackendIds];
  });

  return [...new Set(backendIds)];
}
export function removeRecentHistoryEntries(entriesToRemove = []) {
  const targets = (Array.isArray(entriesToRemove) ? entriesToRemove : [entriesToRemove]).filter(Boolean);
  if (!targets.length) {
    return getRecentHistoryCache() || [];
  }

  const shouldRemove = (entry) => targets.some((target) => isSameRecentHistoryEntry(entry, target));
  const currentUser = AuthUtils.getUser();
  const currentHistory = currentUser?.history || {};
  const recentSearches = currentHistory?.recentSearches || [];
  const remainingRecentSearches = recentSearches.filter((entry) => !shouldRemove(entry));
  if (remainingRecentSearches.length !== recentSearches.length) {
    AuthUtils.updateUser({ history: { ...currentHistory, recentSearches: remainingRecentSearches } });
  }

  [RECENT_SESSION_ONLY_KEY, ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY].forEach((key) => {
    const storedEntries = AppStorage.instance.getFromSessionStorage(key) || [];
    const remainingEntries = storedEntries.filter((entry) => !shouldRemove(entry));
    if (remainingEntries.length === storedEntries.length) {
      return;
    }
    if (remainingEntries.length) {
      AppStorage.instance.setToSessionStorage(key, remainingEntries);
    } else {
      AppStorage.instance.removeFromSessionStorage(key);
    }
  });

  if (recentHistoryCache !== null) {
    recentHistoryCache = recentHistoryCache.filter((entry) => !shouldRemove(entry));
  }
  return getRecentHistoryCache() || [];
}

function getSavedRecentSignatures() {
  return AppStorage.instance.getFromSessionStorage(RECENT_SAVED_SIGNATURES_KEY) || [];
}

function markRecentSignatureSaved(signature) {
  const signatures = getSavedRecentSignatures();
  if (!signatures.includes(signature)) {
    signatures.push(signature);
    AppStorage.instance.setToSessionStorage(RECENT_SAVED_SIGNATURES_KEY, signatures);
  }
}

export function getSessionOnlyRecentHistory() {
  return sortRecentHistoryEntries(AppStorage.instance.getFromSessionStorage(RECENT_SESSION_ONLY_KEY) || [], 'asc');
}

export function getAnonymousRecentHistoryUpgradeSnapshot() {
  return sortRecentHistoryEntries(AppStorage.instance.getFromSessionStorage(ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY) || [], 'asc');
}

function setAnonymousRecentHistoryUpgradeSnapshot(entries) {
  AppStorage.instance.setToSessionStorage(ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY, sortRecentHistoryEntries(entries || [], 'asc'));
}

export function clearAnonymousRecentHistoryUpgradeSnapshot() {
  AppStorage.instance.removeFromSessionStorage(ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY);
}

export function consumeAnonymousRecentHistoryReset() {
  const shouldReset = Boolean(AppStorage.instance.getFromSessionStorage(ANONYMOUS_RECENT_HISTORY_RESET_KEY));
  AppStorage.instance.removeFromSessionStorage(ANONYMOUS_RECENT_HISTORY_RESET_KEY);
  return shouldReset;
}

// The recent-history list on screen is backend data held in Redux (recentHistory, grouped by DB),
// NOT user.history.recentSearches (a client-only breadcrumb list). Flatten the displayed rows so
// the anonymous user's actual recent searches survive the upgrade to a named session.
function getDisplayedRecentHistoryRows() {
  const recentHistory = store.getState().get(historyReducerKey)?.recentHistory || [];
  return recentHistory.flatMap((group) => group?.items || []);
}

export function captureAnonymousRecentHistoryUpgradeSnapshot() {
  const displayedRows = getDisplayedRecentHistoryRows();
  const currentRecentSearches = get(AuthUtils.getUser(), 'history.recentSearches', []);
  const sessionEntries = getSessionOnlyRecentHistory();
  const mergedEntries = mergeRecentHistoryEntries([...displayedRows, ...currentRecentSearches], sessionEntries, 'asc');
  setAnonymousRecentHistoryUpgradeSnapshot(mergedEntries);
  clearRecentHistoryCache();
  AppStorage.instance.setToSessionStorage(ANONYMOUS_RECENT_HISTORY_RESET_KEY, true);
  return mergedEntries;
}

function upsertSessionOnlyRecentHistory(item, sourceRecentHistory) {
  const list = [...getSessionOnlyRecentHistory()];
  // Tag session-only entries so consumers (e.g. NavigationHistory.viewHistory) can skip the
  // backend `restore/results` call, which does not know about these client-side-only records.
  const taggedItem = { ...item, isSessionOnlyRecent: true };
  const taggedBatchKey = getBatchHistoryMergeKey(taggedItem);
  const taggedIdentity = getRecentHistoryIdentity(taggedItem);
  const idx = list.findIndex((entry) => {
    if (isSameRerunSourceRecentSearch(entry, sourceRecentHistory)) {
      return true;
    }
    return taggedBatchKey ? getBatchHistoryMergeKey(entry) === taggedBatchKey : getRecentHistoryIdentity(entry) === taggedIdentity;
  });
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...taggedItem };
  } else {
    list.push(taggedItem);
  }
  AppStorage.instance.setToSessionStorage(RECENT_SESSION_ONLY_KEY, sortRecentHistoryEntries(list, 'asc'));
}

function isSameSessionOnlyRecentEntry(entry = {}, target = {}) {
  if (isSameRerunSourceRecentSearch(entry, target?.rerunSourceRecentHistory)) {
    return true;
  }

  const targetBatchKey = getBatchHistoryMergeKey(target);
  if (targetBatchKey) {
    return getBatchHistoryMergeKey(entry) === targetBatchKey;
  }
  return getRecentHistoryIdentity(entry) === getRecentHistoryIdentity(target);
}

function removeSessionOnlyRecentFromUserHistory(currentUser, item) {
  const recentSearches = currentUser?.history?.recentSearches || [];
  const remainingRecentSearches = recentSearches.filter((entry) => !isSameSessionOnlyRecentEntry(entry, item));
  if (remainingRecentSearches.length === recentSearches.length) {
    return;
  }
  recentSearches.splice(0, recentSearches.length, ...remainingRecentSearches);
  AuthUtils.updateUser({ history: currentUser.history });
}

export function clearRecentSessionStorage() {
  saveTimers.forEach((timer) => clearTimeout(timer));
  saveTimers.clear();
  pendingSaves.clear();
  pendingSavePromises.clear();
  clearRecentHistoryCache();
  AppStorage.instance.removeFromSessionStorage(RECENT_SAVED_SIGNATURES_KEY);
  AppStorage.instance.removeFromSessionStorage(RECENT_SESSION_ONLY_KEY);
  AppStorage.instance.removeFromSessionStorage(ANONYMOUS_RECENT_HISTORY_UPGRADE_SNAPSHOT_KEY);
  AppStorage.instance.removeFromSessionStorage(ANONYMOUS_RECENT_HISTORY_RESET_KEY);
  AppStorage.instance.removeFromSessionStorage(HISTORY_TYPES_SESSION_KEY);
}

export function clearRecentSessionState() {
  store.dispatch({
    type: RECEIVED_RECENT_HISTORY,
    recentHistory: [],
    historyTypes: {},
    totalElements: 0,
    totalPages: 0,
  });
  store.dispatch({
    type: ON_PAGE_CHANGE,
    pageSize: 15,
    currentPage: 1,
  });
  store.dispatch({
    type: HANDLE_SEARCH_INPUT,
    text: '',
  });
  clearRecentSessionStorage();
}

function isQuickSearchBreadcrumb(item) {
  return isQuickSearchHistory(item);
}

function isBackendPersistedRecentHistory(item) {
  // Batch rows stay session-only (sessionStorage) for now: the backend recent-history GET DTO
  // drops the POSTed `queries` payload (spec DATA-009), so a persisted batch row could never be
  // rerun in a later session anyway. Revisit once the backend round-trips `queries`.
  return isQuickSearchBreadcrumb(item) || item?.historyType === 'queryBuilder';
}

function removeZeroResultRowsForSearch(entryToStore, recentSearches) {
  const shouldReplaceZeroResultRows =
    entryToStore?.historyType === 'queryBuilder' && (entryToStore?.resultSize || entryToStore?.totalResult) > 0;
  if (!shouldReplaceZeroResultRows || !entryToStore?.searchId) {
    return;
  }

  const remainingRecentSearches = recentSearches.filter(
    (entry) =>
      !(
        entry?.historyType === 'queryBuilder' &&
        entry.searchId === entryToStore.searchId &&
        (entry.resultSize === 0 || entry.totalResult === 0)
      )
  );
  recentSearches.splice(0, recentSearches.length, ...remainingRecentSearches);
}

function stampRecentHistoryUserTime(item, preserveExisting = false) {
  const userTime = preserveExisting && item?.userTime ? item.userTime : getUtcIsoTimestamp();
  return {
    ...item,
    userTime,
  };
}

function stripTransientRecentHistoryFields(data = {}) {
  const payload = { ...data };
  delete payload.isRerun;
  delete payload.rerunSourceRecentHistory;
  // Only feeds local description generation; the backend GET DTO does not return it (DATA-009),
  // so the generated comment is the durable carrier.
  delete payload.previousResult;
  return payload;
}

function scheduleRecentHistorySave(data, delay = 500) {
  const key = `${getCreationTimeValue(data)}_${data.databaseId}`;
  pendingSaves.set(key, data);
  clearTimeout(saveTimers.get(key));
  saveTimers.set(
    key,
    setTimeout(() => {
      saveTimers.delete(key);
      const pending = pendingSaves.get(key);
      // Skip only exact repeats (same result + sort/order), e.g. refresh / re-entry. A sort change
      // is a new signature and still saves.
      if (!pending) {
        return;
      }

      const signature = recentSaveSignature(pending);
      if (getSavedRecentSignatures().includes(signature)) {
        pendingSaves.delete(key);
        return;
      }

      markRecentSignatureSaved(signature);
      const savePromise = Promise.resolve(
        HistoryResource.saveRecentHistoryApi({
          ...stripTransientRecentHistoryFields(pending),
          userTime: pending.userTime || getUtcIsoTimestamp(pending),
        })
      )
        .then((result) => {
          const savedId = getSavedRecentHistoryId(result);
          if (savedId) {
            pending.id = savedId;
          }
          return result;
        })
        .catch((error) => {
          Logger.error(error);
          return undefined;
        });
      pendingSavePromises.set(key, savePromise);
      savePromise.then(() => {
        pendingSaves.delete(key);
        pendingSavePromises.delete(key);
      });
    }, delay)
  );
}

function sortRecentSearchesAndUpdateUser(recentSearches, currentUser) {
  const sortedRecentSearches = sortRecentHistoryEntries(recentSearches, 'asc');
  recentSearches.splice(0, recentSearches.length, ...sortedRecentSearches);
  AuthUtils.updateUser({ history: currentUser.history });
}

function recordBackendPersistedRecentHistory(entryToStore, recentSearches, currentUser, isRerun) {
  removeZeroResultRowsForSearch(entryToStore, recentSearches);
  recentSearches.push(entryToStore);
  sortRecentSearchesAndUpdateUser(recentSearches, currentUser);
  clearRecentHistoryCache();
  // Reruns are local recency overlays. The recent-history POST creates a new backend row,
  // so do not call it for reruns; merge the overlay with the source row instead.
  if (!isRerun) {
    scheduleRecentHistorySave(entryToStore, 2000);
  }
}
function updateExistingRerunRecentHistory(
  existingRecentSearch,
  additionalActiveBreadcrumb,
  recentSearches,
  currentUser,
  sourceRecentHistory
) {
  Object.assign(existingRecentSearch, additionalActiveBreadcrumb);

  if (isBackendPersistedRecentHistory(existingRecentSearch)) {
    sortRecentSearchesAndUpdateUser(recentSearches, currentUser);
    // Keep rerun recency local. Posting here creates a second backend recent-history row.
  } else {
    upsertSessionOnlyRecentHistory(existingRecentSearch, sourceRecentHistory);
    removeSessionOnlyRecentFromUserHistory(currentUser, existingRecentSearch);
  }
  clearRecentHistoryCache();
}

function updateExistingRecentHistory(existingRecentSearch, additionalActiveBreadcrumb, recentSearches, currentUser) {
  Object.assign(existingRecentSearch, additionalActiveBreadcrumb);

  // Exact-record updates such as filters/sort/order/comments should stay restorable from session
  // storage whenever they are not fully represented by the backend recent-history endpoint.
  if (
    !isBackendPersistedRecentHistory(additionalActiveBreadcrumb) ||
    additionalActiveBreadcrumb.sort ||
    additionalActiveBreadcrumb.order ||
    additionalActiveBreadcrumb.filters?.length
  ) {
    upsertSessionOnlyRecentHistory(existingRecentSearch);
    removeSessionOnlyRecentFromUserHistory(currentUser, existingRecentSearch);
  } else {
    sortRecentSearchesAndUpdateUser(recentSearches, currentUser);
  }
  clearRecentHistoryCache();
}
export function storeRecentHistory(activeBreadcrumb) {
  if (!activeBreadcrumb) return;
  const currentUser = AuthUtils.getUser();
  const isRerun = Boolean(activeBreadcrumb.isRerun);
  const rerunTimestamp = isRerun ? Date.now() : null;
  const additionalActiveBreadcrumb = {
    ...activeBreadcrumb,
    semanticSearchIncluded: activeBreadcrumb.semanticSearchIncluded,
    semanticResultName: activeBreadcrumb.semanticResultName,
    textQuery: activeBreadcrumb.textQuery,
    ...(isRerun
      ? {
          creationTime: rerunTimestamp,
          userTime: getUtcIsoTimestamp(rerunTimestamp),
        }
      : {}),
  };
  // `isRerun` is a transient navigation flag - never persist it on the stored recent-history entry.
  delete additionalActiveBreadcrumb.isRerun;
  const { recentSearches } = currentUser.history;
  const additionalIdentity = getRecentHistoryIdentity(additionalActiveBreadcrumb);
  const existingRecentSearch = isRerun
    ? find(recentSearches, (recentSearch) => isSameRerunRecentSearch(recentSearch, additionalActiveBreadcrumb))
    : find(recentSearches, (recentSearch) => getRecentHistoryIdentity(recentSearch) === additionalIdentity);

  const sourceRecentHistory = additionalActiveBreadcrumb.rerunSourceRecentHistory;
  if (isRerun && !isUndefined(existingRecentSearch)) {
    updateExistingRerunRecentHistory(existingRecentSearch, additionalActiveBreadcrumb, recentSearches, currentUser, sourceRecentHistory);
    return;
  }

  // Keep recent history keyed by its exact record identity. Searches with the same resultName but
  // different timestamps should coexist; the same record should be updated in place.
  // If the source row is not in currentUser history (for example, it came from the backend GET),
  // keep a local rerun overlay even though history breadcrumbs normally suppress new entries.
  const shouldRecordNewEntry = isRerun || !additionalActiveBreadcrumb.isHistoryBreadcrumb;
  if (isUndefined(existingRecentSearch) && shouldRecordNewEntry) {
    const entryToStore = stampRecentHistoryUserTime(additionalActiveBreadcrumb, isRerun);
    if (isBackendPersistedRecentHistory(entryToStore)) {
      recordBackendPersistedRecentHistory(entryToStore, recentSearches, currentUser, isRerun);
    } else {
      // Filtered, sorted, and otherwise derived rows are session-only.
      // Keeping a second copy in currentUser.history makes Recent History show duplicates after
      // merging user and session rows.
      upsertSessionOnlyRecentHistory(entryToStore, sourceRecentHistory);
      removeSessionOnlyRecentFromUserHistory(currentUser, entryToStore);
      clearRecentHistoryCache();
    }
  }
  if (!isUndefined(existingRecentSearch) && !additionalActiveBreadcrumb.isHistoryBreadcrumb) {
    updateExistingRecentHistory(existingRecentSearch, additionalActiveBreadcrumb, recentSearches, currentUser);
  }
}
